"use client";
import React, { forwardRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";
import ValidationPopover, {
  type ValidationErrorT,
  type ValidationPopoverBgT,
} from "./validation-popover";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  // Additional props
  error?:
    | boolean
    | string
    | string[]
    | ValidationErrorT[]
    | (string | ValidationErrorT)[];
  helperText?: string;
  containerClassName?: string;
}

export interface InputWithLabelProps extends InputProps {
  label?: string;
  labelClassName?: string;
  showPasswordToggle?: boolean;
  onValidation?: (field: string, value: string) => void;
  bgVariant?: ValidationPopoverBgT;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      error = false,
      helperText,
      containerClassName,
      ...props
    },
    ref
  ) => {
    // Base input styles
    const inputStyles = cn(
      "bg-input rounded-md px-4 py-3 w-full text-[16px] lg:text-sm",
      "outline-none focus:outline focus:outline-none focus:border-transparent focus:ring-0",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      error ? "outline outline-destructive" : "border-transparent",
      className
    );

    // Determine if there's an error (either boolean true or non-empty string)
    const hasError = Boolean(error);

    // If error is a string, use it as helperText
    const displayHelperText = typeof error === "string" ? error : helperText;

    return (
      <div className="flex w-full flex-col">
        <div className={cn("relative text-sm", containerClassName)}>
          <input type={type} className={inputStyles} ref={ref} {...props} />

          {/* Absolut positionierte Fehlermeldung */}
          {displayHelperText && (
            <p
              className={cn(
                "absolute -bottom-5 left-1 text-xs",
                hasError ? "text-destructive" : "text-foreground/70"
              )}
            >
              {displayHelperText}
            </p>
          )}
        </div>
      </div>
    );
  }
);

const InputWithLabel = forwardRef<HTMLInputElement, InputWithLabelProps>(
  (
    {
      className,
      label,
      labelClassName,
      id,
      type,
      showPasswordToggle = false,
      error,
      onValidation,
      bgVariant = "muted",
      required,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
    const [popoverOpen, setPopoverOpen] = useState(false);

    // Toggle password visibility
    const togglePasswordVisibility = () => {
      setIsPasswordVisible((prev) => !prev);
    };

    // Nur für Passwort-Felder anwenden und wenn Toggle aktiviert ist
    const isPassword = type === "password";
    const showToggle = isPassword && showPasswordToggle;

    // Aktueller Input-Typ basierend auf Sichtbarkeit
    const currentType = isPassword && isPasswordVisible ? "text" : type;

    // Error handling
    const hasError = Boolean(error);

    // Handle different error types
    let errorMessages: (string | ValidationErrorT)[] = [];
    if (typeof error === "string" && error) {
      errorMessages = [error];
    } else if (Array.isArray(error)) {
      errorMessages = error.filter((msg) => {
        if (typeof msg === "string") return msg;
        if (typeof msg === "object" && "message" in msg) {
          return (msg as ValidationErrorT).message;
        }
        return false;
      });
    }

    // Only setup validation handlers if validation is enabled
    const isValidationEnabled = hasError || onValidation;

    // Handle input focus to keep popover open (only if validation enabled)
    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isValidationEnabled && hasError && errorMessages.length > 0) {
        setPopoverOpen(true);
      }
      // Call original onFocus if provided
      props.onFocus?.(e);
    };

    // Handle touch events for mobile devices (only if validation enabled)
    const handleTouchStart = (e: React.TouchEvent<HTMLInputElement>) => {
      if (isValidationEnabled && hasError && errorMessages.length > 0) {
        setPopoverOpen(true);
      }
      // Call original onTouchStart if provided
      props.onTouchStart?.(e);
    };

    // Handle input change to update popover state (only if validation enabled)
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Only handle validation if validation is enabled
      if (isValidationEnabled) {
        // Keep popover open if there are still errors
        if (hasError && errorMessages.length > 0) {
          setPopoverOpen(true);
        }

        // Call validation if provided
        if (onValidation) {
          onValidation(e.target.name, e.target.value);
        }
      }

      // Always call original onChange if provided
      props.onChange?.(e);
    };

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={id} className={cn("pb-0.5 pl-1", labelClassName)}>
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <div className="relative">
          <Input
            id={id}
            className={cn(showToggle && "pr-10", className)}
            type={currentType}
            ref={ref}
            error={showToggle ? undefined : error} // Don't pass error to Input if showToggle is true
            {...props}
            onFocus={isValidationEnabled ? handleInputFocus : props.onFocus}
            onTouchStart={
              isValidationEnabled ? handleTouchStart : props.onTouchStart
            }
            onChange={isValidationEnabled ? handleInputChange : props.onChange}
          />

          {/* Error Icon with Popover - only show if validation is enabled */}
          {isValidationEnabled && hasError && errorMessages.length > 0 && (
            <ValidationPopover
              error={errorMessages as (string | ValidationErrorT)[]}
              className={showToggle ? "right-14" : "right-4"}
              isOpen={popoverOpen}
              onOpenChange={setPopoverOpen}
              triggerRef={ref}
              bgVariant={bgVariant}
            />
          )}

          {/* Password Toggle Button */}
          {showToggle && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              tabIndex={-1}
              className="text-foreground/80 hover:text-foreground absolute top-3.5 right-5 cursor-pointer"
              aria-label={
                isPasswordVisible ? "Passwort verbergen" : "Passwort anzeigen"
              }
            >
              {isPasswordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          )}
        </div>
      </div>
    );
  }
);

Input.displayName = "Input";
InputWithLabel.displayName = "InputWithLabel";

export { Input, InputWithLabel };
